import {
  ArrowBack as BackIcon,
  Cancel as CancelIcon,
  ShoppingCart as CartIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { comparePackages } from "../../../application/actions/packageActions";
import {
  selectPackageComparisonData,
  selectPackageComparisonLoading,
} from "../../../application/selectors/packages";

const PackageComparison = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get package IDs from location state
  const packageIds = location.state?.packageIds || [];

  // Redux State
  const comparisonData = useSelector(selectPackageComparisonData);
  const loading = useSelector(selectPackageComparisonLoading);

  useEffect(() => {
    if (packageIds.length >= 2) {
      dispatch(comparePackages(packageIds));
    }
  }, [packageIds, dispatch]);

  const handleBackToList = () => {
    navigate("/packages");
  };

  const handleSelectPackage = (packageId) => {
    navigate("/orders/create", {
      state: { packageId },
    });
  };

  if (packageIds.length < 2) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          لطفاً حداقل ۲ پکیج برای مقایسه انتخاب کنید
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست پکیج‌ها
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!comparisonData || comparisonData.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          خطا در بارگذاری اطلاعات مقایسه
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          بازگشت به لیست پکیج‌ها
        </Button>
      </Container>
    );
  }

  // Helper function to render comparison value
  const renderComparisonValue = (value, type = "text") => {
    if (value === null || value === undefined) {
      return <Typography variant="body2">-</Typography>;
    }

    switch (type) {
      case "price":
        return (
          <Typography variant="body1" fontWeight="bold" color="primary">
            {value.toLocaleString("fa-IR")} ریال
          </Typography>
        );
      case "boolean":
        return value ? (
          <CheckIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        );
      case "number":
        return (
          <Typography variant="body1">
            {value.toLocaleString("fa-IR")}
          </Typography>
        );
      default:
        return <Typography variant="body1">{value}</Typography>;
    }
  };

  // Find best value for highlighting
  const getBestPackageId = (field) => {
    const values = comparisonData.map((pkg) => ({
      id: pkg.id,
      value: pkg[field],
    }));

    switch (field) {
      case "totalPrice":
        // Lower is better
        return values.reduce((min, curr) =>
          curr.value < min.value ? curr : min
        ).id;
      case "capacity":
      case "warrantyYears":
      case "estimatedYearlyProduction":
        // Higher is better
        return values.reduce((max, curr) =>
          curr.value > max.value ? curr : max
        ).id;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToList}
          sx={{ mb: 2 }}
        >
          بازگشت به لیست
        </Button>

        <Typography variant="h4" gutterBottom>
          مقایسه پکیج‌های خورشیدی
        </Typography>
        <Typography variant="body1" color="text.secondary">
          مقایسه {comparisonData.length} پکیج انتخاب شده
        </Typography>
      </Box>

      {/* Package Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {comparisonData.map((pkg) => (
          <Grid item xs={12} md={6} lg={3} key={pkg.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                component="img"
                src={pkg.imageUrl || "/default-package.jpg"}
                alt={pkg.name}
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                }}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom noWrap>
                  {pkg.name}
                </Typography>
                <Typography
                  variant="h5"
                  color="primary"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  {pkg.totalPrice?.toLocaleString("fa-IR")} ریال
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CartIcon />}
                  onClick={() => handleSelectPackage(pkg.id)}
                  disabled={!pkg.isActive}
                >
                  انتخاب این پکیج
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Comparison Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  مشخصه
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell
                    key={pkg.id}
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {pkg.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Capacity */}
              <TableRow>
                <TableCell>
                  <strong>ظرفیت (kW)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell
                    key={pkg.id}
                    align="center"
                    sx={{
                      backgroundColor:
                        getBestPackageId("capacity") === pkg.id
                          ? "success.light"
                          : "inherit",
                    }}
                  >
                    {renderComparisonValue(pkg.capacity, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Price */}
              <TableRow>
                <TableCell>
                  <strong>قیمت</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell
                    key={pkg.id}
                    align="center"
                    sx={{
                      backgroundColor:
                        getBestPackageId("totalPrice") === pkg.id
                          ? "success.light"
                          : "inherit",
                    }}
                  >
                    {renderComparisonValue(pkg.totalPrice, "price")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Number of Panels */}
              <TableRow>
                <TableCell>
                  <strong>تعداد پنل</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.numberOfPanels, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Panel Wattage */}
              <TableRow>
                <TableCell>
                  <strong>قدرت هر پنل (وات)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.panelWattage, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Inverter Capacity */}
              <TableRow>
                <TableCell>
                  <strong>ظرفیت اینورتر (kW)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.inverterCapacity, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Warranty Years */}
              <TableRow>
                <TableCell>
                  <strong>مدت گارانتی (سال)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell
                    key={pkg.id}
                    align="center"
                    sx={{
                      backgroundColor:
                        getBestPackageId("warrantyYears") === pkg.id
                          ? "success.light"
                          : "inherit",
                    }}
                  >
                    {renderComparisonValue(pkg.warrantyYears, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Estimated Yearly Production */}
              <TableRow>
                <TableCell>
                  <strong>تولید سالانه تخمینی (kWh)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell
                    key={pkg.id}
                    align="center"
                    sx={{
                      backgroundColor:
                        getBestPackageId("estimatedYearlyProduction") === pkg.id
                          ? "success.light"
                          : "inherit",
                    }}
                  >
                    {renderComparisonValue(
                      pkg.estimatedYearlyProduction,
                      "number"
                    )}
                  </TableCell>
                ))}
              </TableRow>

              {/* ROI Years */}
              <TableRow>
                <TableCell>
                  <strong>بازگشت سرمایه (سال)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.estimatedROIYears, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Installation Area */}
              <TableRow>
                <TableCell>
                  <strong>مساحت مورد نیاز (m²)</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.installationArea, "number")}
                  </TableCell>
                ))}
              </TableRow>

              {/* Active Status */}
              <TableRow>
                <TableCell>
                  <strong>وضعیت</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    <Chip
                      label={pkg.isActive ? "فعال" : "غیرفعال"}
                      color={pkg.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                ))}
              </TableRow>

              {/* Contractor */}
              <TableRow>
                <TableCell>
                  <strong>پیمانکار</strong>
                </TableCell>
                {comparisonData.map((pkg) => (
                  <TableCell key={pkg.id} align="center">
                    {renderComparisonValue(pkg.contractor?.companyName || "-")}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<BackIcon />}
          onClick={handleBackToList}
        >
          بازگشت به لیست پکیج‌ها
        </Button>
      </Box>
    </Container>
  );
};

export default PackageComparison;
